import { useForm } from "react-hook-form";
import { Button, PasswordSwitcherButton } from "@/components/Button";
import { Input } from "@/components/Input";
import { usePostResetPassword } from "@/requests/hooks/user";
import parseQueryString from "@/utils/parseQueryString";
import { Form } from "@/components/Form";
import { object, string, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { H3 } from "@/components/Typography";
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/pro-duotone-svg-icons";

const formSchema = object({
  newPassword: string()
    .label(t("auth.resetPassword.form.newPassword.label"))
    .required(t("auth.resetPassword.form.newPassword.errors.required"))
    .min(10, t("auth.resetPassword.form.newPassword.errors.length")),
});

type IResetPasswordForm = InferType<typeof formSchema>;

const ResetPassword = () => {
  const { token } = parseQueryString();
  const { mutate, isSuccess, isPending } = usePostResetPassword();
  const navigate = useNavigate();
  const [showPasswordInput, setShowPasswordInput] = useState<boolean>(false);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  const methods = useForm<IResetPasswordForm>({
    resolver: yupResolver(formSchema),
  });
  const {
    register,
    handleSubmit,
    formState: {
      errors: { newPassword },
    },
  } = methods;

  useEffect(() => {
    if (isSuccess) {
      navigate("/auth/login");
      toast.success(t("auth.resetPassword.success"));
    }
  }, [isSuccess]);

  if (!token) return <Navigate to="/auth/login" />;

  const onSubmit = ({ newPassword }: IResetPasswordForm) => {
    mutate({ token, newPassword });
  };
  const { ref, ...rest } = register("newPassword");

  const resendVerificationLink = () => {
    navigate("/auth/forgot-password");
  };

  const handleSharedRef = (e: HTMLInputElement | null) => {
    ref(e);
    passwordRef.current = e;
  };

  const toggleInputType = () =>
    setShowPasswordInput(setShowPassword => !setShowPassword);

  const renderPasswordSwitcher = () => (
    <PasswordSwitcherButton
      showPassword={showPasswordInput}
      onClick={toggleInputType}
    />
  );

  return (
    <div className="text-center">
      <div className="relative">
        <Link to="/auth/login">
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="absolute left-0 top-10"
          />
        </Link>
        <H3 className="flex-grow text-center font-semibold">
          {t("auth.resetPassword.title")}
        </H3>
      </div>
      <p className="mb-20 text-gray-dark">
        {t("auth.resetPassword.description")}
      </p>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="mt-5 flex flex-col gap-15">
        <Input
          label={t("auth.resetPassword.form.newPassword.label")}
          {...rest}
          type={showPasswordInput ? "text" : "password"}
          helperText={newPassword?.message}
          variant={!!newPassword?.message ? "error" : undefined}
          testId="password-input"
          helperTextTestId="password-input-helper-text"
          ref={handleSharedRef}
          trailingSlot={renderPasswordSwitcher()}
        />

        <div className="mt-10 flex flex-col items-center gap-10">
          <Button
            variant="secondary-outline"
            onClick={resendVerificationLink}
            width="full"
            type="button">
            {t("auth.resetPassword.buttons.resendLink")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            width="full"
            disabled={!!newPassword?.message}
            testId="submit-button"
            loading={isPending}>
            {t("auth.resetPassword.buttons.update")}
          </Button>
        </div>
      </Form>
    </div>
  );
};
export default ResetPassword;
